import type { LocalUserPreferences } from '@cobliteam/api-lib/src/account/user/Types';
import type { AccessibleFleet } from '@cobliteam/common-types';
import type { RouteDef } from '@cobliteam/dashboard/src/dashboardDef';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useFleetCompanyOnboardingStatus, useLocalStorage } from '@cobliteam/hooks';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags/useFeatureFlags';
import { cn } from '@cobliteam/ui/utils';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import AppFleetSwitcher from '../AppFleetSwitcher';
import OnboardingActivationTasksPopover from '../AppHeader/components/ActivationTasks/Onboarding/OnboardingActivationTasksPopover';
import OngoingActivationTasksPopover from '../AppHeader/components/ActivationTasks/Ongoing/OngoingActivationTasksPopover';
import BoletosButton from '../AppHeader/components/BoletosButton';
import HowToUseButton from '../AppHeader/components/HowToUseButton';
import Incidents from '../AppHeader/components/Incidents';
import NewsFeed from '../AppHeader/components/NewsFeed';
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarSeparator, useSidebar } from './components/Sidebar';
import { SidebarMenu } from './components/SidebarMenu';
import { SidebarMenuMobile } from './components/SidebarMenuMobile';

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  fleets: AccessibleFleet[];
  currentFleet: AccessibleFleet;
}

function AppSidebar({ fleets, currentFleet, ...props }: AppSidebarProps) {
  const { sidebar } = useDashboard();
  const { openMobile, isCollapsed, isMobile } = useSidebar();
  const [primaryHovered, setPrimaryHovered] = useState<RouteDef | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // primary sidebar
  const primarySidebar = sidebar.router.filter((route) => route.sidebar?.hide !== true);
  const mainSidebar = primarySidebar.slice(0, primarySidebar.length - 1); // remove last two items
  const footerSidebar = primarySidebar.slice(primarySidebar.length - 1); // last two items
  const hasSecondarySidebar = !!primaryHovered?.routes && !!(primaryHovered.routes.length > 0) && primaryHovered.routes.some((route) => route.sidebar?.hide !== true);

  const [preferences] = useLocalStorage<LocalUserPreferences>('localUserPreferences');
  const { ongoingActivationTasks } = useFeatureFlags();

  const { data: onboardingStatus } = useFleetCompanyOnboardingStatus(currentFleet.id);

  const shouldShowOngoing = useMemo(() => {
    return ongoingActivationTasks;
  }, [ongoingActivationTasks]);

  const shouldShowOnboarding = useMemo(() => {
    return !shouldShowOngoing && onboardingStatus?.hasAnyOnboardingContract && onboardingStatus?.hasAnyContractSignedInLast90Days && !preferences?.onboardingCompleted;
  }, [shouldShowOngoing, onboardingStatus, preferences?.onboardingCompleted]);

  useEffect(() => {
    const hoveredPrimary = isCollapsed ? null : sidebar.primary;
    setPrimaryHovered(hoveredPrimary || null);
  }, [isCollapsed, sidebar.primary]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = (route: RouteDef | null) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setPrimaryHovered(route);
    }, 500);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  if (!primarySidebar) return null;

  if (isMobile) {
    return (
      <Sidebar variant='sidebar' {...props}>
        <SidebarHeader>
          <AppFleetSwitcher fleets={fleets} currentFleet={currentFleet} />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent
              className={cn(
                'scrollbar',
                'scrollbar-thumb-custom',
                'scrollbar-thin',
                'scrollbar-thumb-rounded-full scrollbar-track-rounded-full',
                'scrollbar-thumb-gray-11',
                'scrollbar-thumb-bg-clip-content',
                'scrollbar-track-gray-12'
              )}
            >
              <SidebarMenuMobile />
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    );
  }

  return (
    <>
      {/* Primary sidebar */}
      <Sidebar
        collapsible={isCollapsed ? 'none' : 'icon'}
        onMouseLeave={() => setPrimaryHovered(isCollapsed ? null : sidebar.primary || null)}
        hasSecondary={hasSecondarySidebar}
        variant='sidebar'
        className={cn('flex overflow-hidden [&>[data-sidebar=sidebar]]:flex-row', isCollapsed && '!w-[--sidebar-width-icon]')}
        {...props}
      >
        <Sidebar id='primary-sidebar' collapsible='none' className={cn('!w-[--sidebar-width-icon] h-screen bg-gray-1')}>
          {/* Header */}
          <SidebarHeader>
            <AppFleetSwitcher fleets={fleets} currentFleet={currentFleet} />
          </SidebarHeader>
          <SidebarSeparator className={cn('my-2', openMobile && 'mb-0')} />

          {/* Main */}
          <SidebarContent className='h-[calc(100vh-144px)]'>
            <SidebarGroup>
              <SidebarGroupContent className='mt-2 space-y-4'>
                {!openMobile && <SidebarMenu primary primaryDef={mainSidebar} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} hoveredRoute={primaryHovered} />}
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          {/* Footer */}
          <SidebarFooter>
            {!openMobile && (
              <SidebarGroup>
                <SidebarGroupContent className='flex flex-col items-center justify-center pb-2.5'>
                  {shouldShowOngoing && <OngoingActivationTasksPopover />}
                  {shouldShowOnboarding && <OnboardingActivationTasksPopover />}
                  <Incidents />
                  <BoletosButton />
                  <NewsFeed />
                  <HowToUseButton />
                  <SidebarMenu primary showTitle={false} primaryDef={footerSidebar} onMouseEnter={setPrimaryHovered} hoveredRoute={primaryHovered} />
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </SidebarFooter>
        </Sidebar>

        {/* Secondary sidebar */}
        <Sidebar
          id='secondary-sidebar'
          collapsible='none'
          className={cn(
            'left-0 w-[--sidebar-width-secondary] overflow-hidden border-none',
            !hasSecondarySidebar && 'hidden',
            isCollapsed && '!w-[--sidebar-width-secondary] absolute top-0 left-[74px] z-[1001] h-screen border-solid shadow-lg'
          )}
        >
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu primaryDef={primarySidebar} primaryHovered={primaryHovered || undefined} />
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>
      </Sidebar>
    </>
  );
}

export default AppSidebar;
